import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { GraduationCap, Users, Calendar, Award, Send, CheckCircle } from 'lucide-react';

const CAMP_OPTIONS = [
  {
    id: 'beginner',
    name: 'Beginner Camp',
    description: 'Perfect for newcomers. Learn the basics of officiating.',
    duration: '2 Days',
    features: ['Rule fundamentals', 'Positioning basics', 'Game mechanics', 'Certificate of completion']
  },
  {
    id: 'intermediate',
    name: 'Intermediate Camp',
    description: 'Take your skills to the next level.',
    duration: '3 Days',
    features: ['Advanced positioning', 'Crew communication', 'Game management', 'Live game practice']
  },
  {
    id: 'advanced',
    name: 'Elite Camp',
    description: 'For experienced refs looking to work top-tier games.',
    duration: '5 Days',
    features: ['Elite game scenarios', 'Film review sessions', 'Mentorship program', 'Placement assistance']
  }
];

export const RefCampPage = () => {
  const { api, user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience_level: 'beginner',
    message: ''
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchInquiries();
    }
  }, [api, isAdmin]);

  const fetchInquiries = async () => {
    try {
      const response = await api().get('/refcamp/inquiries');
      setInquiries(response.data);
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await api().post('/refcamp/inquiry', formData);
      setSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit inquiry');
    }
  };

  return (
    <div data-testid="refcamp-page" className="space-y-12 animate-fade-in">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-zinc-900 to-zinc-800 border border-zinc-700 p-8 md:p-12 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-2 text-emerald-400 mb-4">
            <GraduationCap className="w-6 h-6" />
            <span className="font-bold uppercase tracking-wider">Ref Camp</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-wide mb-4">
            Wanna Be a Ref?<br />
            <span className="text-emerald-400">Join Our Camp Today!</span>
          </h1>
          <p className="text-zinc-400 text-lg mb-6">
            Learn from the best. Our comprehensive training programs will prepare you for officiating 
            at any level. Whether you're just starting out or looking to advance your career.
          </p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="inquiry-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wide px-8 py-6 text-lg rounded-sm"
              >
                <Send className="w-5 h-5 mr-2" />
                Inquire Now
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase">Camp Inquiry</DialogTitle>
              </DialogHeader>
              
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-2">Thank You!</h3>
                  <p className="text-zinc-400">We'll be in touch soon to discuss your training options.</p>
                  <Button
                    onClick={() => { setDialogOpen(false); setSubmitted(false); }}
                    className="mt-6 bg-white text-black hover:bg-zinc-200"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Full Name *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      data-testid="inquiry-name"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email *</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@email.com"
                        required
                        data-testid="inquiry-email"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Phone *</label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        required
                        data-testid="inquiry-phone"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Experience Level</label>
                    <Select
                      value={formData.experience_level}
                      onValueChange={(v) => setFormData({ ...formData, experience_level: v })}
                    >
                      <SelectTrigger data-testid="inquiry-level" className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Experience</SelectItem>
                        <SelectItem value="beginner">Beginner (0-1 years)</SelectItem>
                        <SelectItem value="intermediate">Intermediate (1-3 years)</SelectItem>
                        <SelectItem value="advanced">Advanced (3+ years)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Message (Optional)</label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your goals..."
                      data-testid="inquiry-message"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="submit-inquiry-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase"
                  >
                    Submit Inquiry
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Camp Options */}
      <div>
        <h2 className="text-2xl font-heading font-bold uppercase tracking-wide mb-6 text-center">
          Choose Your Path
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CAMP_OPTIONS.map((camp) => (
            <div
              key={camp.id}
              data-testid={`camp-option-${camp.id}`}
              className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-emerald-900 transition-colors"
            >
              <div className="flex items-center gap-2 text-emerald-400 mb-3">
                <Award className="w-5 h-5" />
                <span className="text-sm uppercase tracking-wider">{camp.duration}</span>
              </div>
              <h3 className="text-xl font-heading font-bold mb-2">{camp.name}</h3>
              <p className="text-zinc-400 text-sm mb-4">{camp.description}</p>
              <ul className="space-y-2">
                {camp.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 p-6 text-center">
          <p className="text-3xl font-heading font-bold text-emerald-400">500+</p>
          <p className="text-sm text-zinc-500 uppercase tracking-wider">Refs Trained</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 p-6 text-center">
          <p className="text-3xl font-heading font-bold text-emerald-400">15+</p>
          <p className="text-sm text-zinc-500 uppercase tracking-wider">Years Experience</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 p-6 text-center">
          <p className="text-3xl font-heading font-bold text-emerald-400">98%</p>
          <p className="text-sm text-zinc-500 uppercase tracking-wider">Placement Rate</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 p-6 text-center">
          <p className="text-3xl font-heading font-bold text-emerald-400">12</p>
          <p className="text-sm text-zinc-500 uppercase tracking-wider">Camp Sessions/Year</p>
        </div>
      </div>

      {/* Admin: View Inquiries */}
      {isAdmin && inquiries.length > 0 && (
        <div>
          <h2 className="text-xl font-heading font-bold uppercase tracking-wide mb-4">Recent Inquiries</h2>
          <div className="bg-zinc-900/50 border border-zinc-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800 text-xs uppercase tracking-wider text-zinc-400">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-left">Level</th>
                  <th className="px-4 py-3 text-left">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {inquiries.map((inquiry) => (
                  <tr key={inquiry.id} className="hover:bg-zinc-800/50">
                    <td className="px-4 py-3">{inquiry.name}</td>
                    <td className="px-4 py-3 text-zinc-400">{inquiry.email}</td>
                    <td className="px-4 py-3 text-zinc-400">{inquiry.phone}</td>
                    <td className="px-4 py-3 capitalize">{inquiry.experience_level}</td>
                    <td className="px-4 py-3 text-zinc-500">
                      {new Date(inquiry.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
